// components/Login.tsx
// Login page component with email/password authentication

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

export const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resetMode, setResetMode] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetSent, setResetSent] = useState(false);
    const { signIn, resetPassword } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !password) {
            setError('Please enter both email and password');
            return;
        }

        try {
            setError('');
            setLoading(true);
            await signIn(email, password);
            navigate('/dashboard');
        } catch (err: any) {
            setError(err.message || 'Failed to sign in. Please try again');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!resetEmail) {
            setError('Please enter your email address');
            return;
        }

        try {
            setError('');
            setLoading(true);
            await resetPassword(resetEmail);
            setResetSent(true);
        } catch (err: any) {
            setError('Failed to send reset email. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        setResetMode(false);
        setResetSent(false);
        setResetEmail('');
        setError('');
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h1>Omore Admin</h1>
                    <p>Content Dashboard</p>
                </div>

                {resetMode ? (
                    <div>
                        {resetSent ? (
                            <div className="reset-success">
                                <p>Password reset email sent to <strong>{resetEmail}</strong>. Check your inbox.</p>
                                <button className="forgot-password-link" onClick={handleBackToLogin}>
                                    Back to login
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleResetPassword} className="login-form">
                                {error && <div className="error-message">{error}</div>}
                                <div className="form-group">
                                    <label htmlFor="reset-email">Email</label>
                                    <input
                                        id="reset-email"
                                        type="email"
                                        value={resetEmail}
                                        onChange={(e) => setResetEmail(e.target.value)}
                                        placeholder=""
                                        disabled={loading}
                                        autoComplete="email"
                                        autoFocus
                                    />
                                </div>
                                <button type="submit" className="login-button" disabled={loading}>
                                    {loading ? 'Sending...' : 'Send reset email'}
                                </button>
                                <button type="button" className="forgot-password-link" onClick={handleBackToLogin}>
                                    Back to login
                                </button>
                            </form>
                        )}
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="login-form">
                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder=""
                                disabled={loading}
                                autoComplete="email"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                disabled={loading}
                                autoComplete="current-password"
                            />
                        </div>

                        <button
                            type="submit"
                            className="login-button"
                            disabled={loading}
                        >
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>

                        <button
                            type="button"
                            className="forgot-password-link"
                            onClick={() => { setResetMode(true); setError(''); }}
                        >
                            Forgot password?
                        </button>
                    </form>
                )}

                <div className="login-footer">
                    <p>Authorized personnel only</p>
                </div>
            </div>
        </div>
    );
};
